import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, ChevronDownIcon, Save } from "lucide-react";

import { Discount, Customer } from "@/lib/supabase";
import { customerService, discountService } from "@/lib/database";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function NewDiscountPage() {
  const navigate = useNavigate();

  const [newDiscount, setNewDiscount] = useState<Partial<Discount>>({
    code: "",
    description: "",
    discount_type: "percentage",
    amount: 0,
    is_stackable: false,
    is_active: true,
    usage_limit_per_customer: 0,
    total_usage_limit: 0,
  });

  const [assignedCustomerId, setAssignedCustomerId] = useState<string | null>(
    null
  );
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [_, setLoading] = useState<boolean>(false);
  const [openStartDatePicker, setOpenStartDatePicker] = useState(false);
  const [openEndDatePicker, setOpenEndDatePicker] = useState(false);
  const [hasValidityPeriod, setHasValidityPeriod] = useState(false);
  const [hasUsageLimits, setHasUsageLimits] = useState(false);
  const [startTime, setStartTime] = useState<string>("00:00:00");
  const [endTime, setEndTime] = useState<string>("00:00:00");

  const handleCreateDiscount = async () => {
    try {
      setLoading(true);

      // Combine date and time before converting to ISO string
      const startDateTime =
        hasValidityPeriod && startDate
          ? new Date(`${startDate.toISOString().split("T")[0]}T${startTime}`)
          : null;

      const endDateTime =
        hasValidityPeriod && endDate
          ? new Date(`${endDate.toISOString().split("T")[0]}T${endTime}`)
          : null;

      if (hasValidityPeriod) {
        if (!startDateTime || !endDateTime) {
          throw new Error(
            "Please select both start and end dates for the validity period."
          );
        }
      }

      const payload: any = {
        code: newDiscount.code?.trim().toUpperCase(),
        description: newDiscount.description || null,
        discount_type: newDiscount.discount_type,
        amount: newDiscount.amount,
        is_stackable: newDiscount.is_stackable,
        is_active: newDiscount.is_active,
        usage_limit_per_customer: hasUsageLimits
          ? newDiscount.usage_limit_per_customer
          : null,
        total_usage_limit: hasUsageLimits
          ? newDiscount.total_usage_limit
          : null,
        start_date:
          hasValidityPeriod && startDateTime
            ? startDateTime.toISOString()
            : null,
        end_date:
          hasValidityPeriod && endDateTime ? endDateTime.toISOString() : null,
      };

      await discountService.create(payload, assignedCustomerId || undefined);

      navigate("/admin/discounts-promos");
    } catch (error) {
      console.error("Failed to create discount:", error);

      toast.error(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred.",
        { position: "top-center" }
      );
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // TODO: Only load last 10 and allow searching in the select box
      // Load all customers for dropdown
      const { customers: customerData } = await customerService.getAll(1000);

      setCustomers(customerData || []);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex space-x-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Create New Discount
            </h1>
            <p className="text-gray-600">
              Fill in the details to create a new customer-bound discount
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Discount Information</CardTitle>
              <CardDescription>Basic discount details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={newDiscount.code}
                  onChange={(e) =>
                    setNewDiscount((prev: Partial<Discount>) => ({
                      ...prev,
                      code: e.target.value,
                    }))
                  }
                />

                <p>{newDiscount.code?.toUpperCase()}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newDiscount.description}
                  onChange={(e) =>
                    setNewDiscount((prev: Partial<Discount>) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">Discount Type *</Label>
                  <Select
                    value={newDiscount.discount_type}
                    onValueChange={(value: "percentage" | "fixed") =>
                      setNewDiscount((prev: Partial<Discount>) => ({
                        ...prev,
                        discount_type: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    type="number"
                    id="amount"
                    value={newDiscount.amount || ""}
                    inputMode="decimal"
                    onChange={(e) =>
                      setNewDiscount((prev: Partial<Discount>) => ({
                        ...prev,
                        amount: parseFloat(e.target.value) || undefined,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-3 mt-4">
                <div className="space-x-2 items-center flex">
                  <Checkbox
                    checked={hasUsageLimits}
                    id="usage-limits"
                    onCheckedChange={(checked: boolean) => {
                      setHasUsageLimits(checked);

                      if (!checked) {
                        setNewDiscount((prev: Partial<Discount>) => ({
                          ...prev,
                          usage_limit_per_customer: 0,
                          total_usage_limit: 0,
                        }));
                      }
                    }}
                  />
                  <Label htmlFor="usage-limits">Usage Limits</Label>
                </div>

                {hasUsageLimits && (
                  <div className="flex gap-4 flex-wrap justify-between">
                    <div className="flex flex-col gap-3">
                      <Label
                        htmlFor="usage-limit-per-customer"
                        className="px-1"
                      >
                        Usage Limit Per Customer
                      </Label>

                      <Input
                        type="number"
                        id="usage-limit-per-customer"
                        value={newDiscount.usage_limit_per_customer || ""}
                        inputMode="numeric"
                        className="w-20"
                        onChange={(e) =>
                          setNewDiscount((prev: Partial<Discount>) => ({
                            ...prev,
                            usage_limit_per_customer:
                              parseFloat(e.target.value) || 0,
                          }))
                        }
                      />
                    </div>

                    <div className="flex flex-col gap-3 sm:items-end">
                      <Label htmlFor="total-usage-limit" className="px-1">
                        Total Usage Cap
                      </Label>

                      <Input
                        type="number"
                        id="total-usage-limit"
                        value={newDiscount.total_usage_limit || ""}
                        inputMode="numeric"
                        className="w-20"
                        onChange={(e) =>
                          setNewDiscount((prev: Partial<Discount>) => ({
                            ...prev,
                            total_usage_limit: parseFloat(e.target.value) || 0,
                          }))
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3 mt-4">
                <div className="space-x-2 items-center flex">
                  <Checkbox
                    checked={hasValidityPeriod}
                    id="validity-period"
                    onCheckedChange={(checked: boolean) => {
                      setHasValidityPeriod(checked);

                      if (!checked) {
                        setStartDate(undefined);
                        setEndDate(undefined);
                      }
                    }}
                  />
                  <Label htmlFor="validity-period">Validity Period</Label>
                </div>

                {hasValidityPeriod && (
                  <div className="flex flex-col gap-4 md:flex-row flex-wrap">
                    <div className="flex gap-4 justify-between">
                      <div className="flex flex-col gap-3">
                        <Label htmlFor="date-picker" className="px-1">
                          Start Date
                        </Label>
                        <Popover
                          open={openStartDatePicker}
                          onOpenChange={setOpenStartDatePicker}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              id="date-picker"
                              className="w-32 justify-between font-normal"
                            >
                              {startDate
                                ? startDate.toLocaleDateString()
                                : "Select date"}
                              <ChevronDownIcon />
                            </Button>
                          </PopoverTrigger>

                          <PopoverContent
                            className="w-auto overflow-hidden p-0"
                            align="start"
                          >
                            <Calendar
                              mode="single"
                              selected={startDate}
                              captionLayout="dropdown"
                              onSelect={(date) => {
                                setStartDate(date);
                                setOpenStartDatePicker(false);
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="flex flex-col gap-3">
                        <Label htmlFor="start-time-picker" className="px-1">
                          Start Time
                        </Label>

                        <Input
                          type="time"
                          id="start-time-picker"
                          step="1"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                        />
                      </div>
                    </div>

                    <div className="flex gap-4 justify-between">
                      <div className="flex flex-col gap-3">
                        <Label htmlFor="date-picker" className="px-1">
                          End Date
                        </Label>

                        <Popover
                          open={openEndDatePicker}
                          onOpenChange={setOpenEndDatePicker}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              id="date-picker"
                              className="w-32 justify-between font-normal"
                            >
                              {endDate
                                ? endDate.toLocaleDateString()
                                : "Select date"}
                              <ChevronDownIcon />
                            </Button>
                          </PopoverTrigger>

                          <PopoverContent
                            className="w-auto overflow-hidden p-0"
                            align="start"
                          >
                            <Calendar
                              mode="single"
                              selected={endDate}
                              captionLayout="dropdown"
                              onSelect={(date) => {
                                setEndDate(date);
                                setOpenEndDatePicker(false);
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="flex flex-col gap-3">
                        <Label htmlFor="end-time-picker" className="px-1">
                          End Time
                        </Label>

                        <Input
                          type="time"
                          id="end-time-picker"
                          step="1"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-x-2 items-center flex">
                <Switch
                  id="stackable"
                  checked={newDiscount.is_stackable}
                  onCheckedChange={(checked) =>
                    setNewDiscount((prev: Partial<Discount>) => ({
                      ...prev,
                      is_stackable: checked,
                    }))
                  }
                />

                <div className="flex flex-col">
                  <Label htmlFor="stackable">Stackable</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow this discount to be used with other discounts & promo
                    codes
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Assign to Customer</CardTitle>
              <CardDescription>
                Select the first customer to bind this discount to.
                <br />
                <strong>Optional.</strong> You can always assign customers
                later.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Select
                value={assignedCustomerId || undefined}
                onValueChange={(value) => setAssignedCustomerId(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers?.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.first_name} {customer.last_name} -{" "}
                      {customer.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>
      </div>

      <Button
        onClick={handleCreateDiscount}
        className="bg-[#254164] hover:bg-[#1a2d47] w-full"
      >
        <Save className="h-4 w-4 mr-2" />
        Create Discount
      </Button>
    </div>
  );
}
